import { useCallback } from "react";

import CopyFieldDropdownMenuItem from "components/CopyField/DropdownMenuItem";
import { HUMANIZE_PROVIDER } from "constants/vcs_providers";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageVCS } from "hooks/useAnalytics/pages/vcs";
import { VcsIntegration } from "types/generated";

import { showVCSDeleteIntegrationModal } from "../VcsShared/components/DeleteIntegrationModal";
import { isTestIntegrationConnectionAvailable } from "../VcsShared/helpers";
import useDeleteVcsIntegration from "../VcsShared/useDeleteVcsIntegration";
import useTestVCSIntegrationConnection from "../VcsShared/useTestVCSIntegrationConnection";

type VCSIntegrationDropdownActionsProps = {
  integration: VcsIntegration;
  onEdit: () => void;
  totalEntitiesCount: number;
  onDelete: () => void;
};

const VCSIntegrationDropdownActions = ({
  integration,
  onEdit,
  totalEntitiesCount,
  onDelete,
}: VCSIntegrationDropdownActionsProps) => {
  const { testIntegrationConnection, loading } = useTestVCSIntegrationConnection({
    id: integration.id,
    name: integration.name || `Default ${HUMANIZE_PROVIDER[integration.provider]}`,
    provider: integration.provider,
  });

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageVCS.VCSIntegrations,
  });

  const { deleteVcsIntegration } = useDeleteVcsIntegration();

  const isTestConnectionAvailable = isTestIntegrationConnectionAvailable(integration.details);

  const handleDeleteVcsIntegration = useCallback(() => {
    // FYI: the ID is not available only for the built-in GitHub integration & the delete option is not available for it
    if (integration.id) {
      deleteVcsIntegration(integration.id, integration.provider, totalEntitiesCount, onDelete);
    }
  }, [deleteVcsIntegration, onDelete, integration.id, integration.provider, totalEntitiesCount]);

  const onDeleteConfirmationModalShow = useCallback(() => {
    showVCSDeleteIntegrationModal({
      attachedEntitiesQty: totalEntitiesCount,
      integrationName: integration.name,
      onConfirm: handleDeleteVcsIntegration,
    });

    trackSegmentAnalyticsEvent("Integration - Unlink Start");
  }, [
    handleDeleteVcsIntegration,
    integration.name,
    totalEntitiesCount,
    trackSegmentAnalyticsEvent,
  ]);

  return (
    <DropdownMenuEllipsis tooltip="Integration actions" dotsSize="medium">
      <CopyFieldDropdownMenuItem title="Copy ID" value={integration.id} />
      {isTestConnectionAvailable && (
        <DropdownMenuItem onAction={testIntegrationConnection} loading={loading}>
          Test
        </DropdownMenuItem>
      )}

      <DropdownMenuItem onAction={onEdit}>Edit</DropdownMenuItem>
      <DropdownMenuItem onAction={onDeleteConfirmationModalShow} danger>
        Delete
      </DropdownMenuItem>
    </DropdownMenuEllipsis>
  );
};

export default VCSIntegrationDropdownActions;
